import { AfterViewInit, Component, inject, Input, OnDestroy } from '@angular/core';
import * as echarts from 'echarts';
import { applyRoleVisibilityToOption } from '../../utils/chart-role-visibility.utils';
import { Subscription } from 'rxjs';
import { ConfigService } from '../../shared-services/config.service';
import { ChartFilterHandlerService } from '../../shared-services/chart-filter-handler.service';
import { GlobalFilterService } from '../../shared-services/global-filter-service';
import { ChartWrapperService, ChartWrapperInstance } from '../../shared-services/chart-wrapper.service';
import { ResponsiveLayoutService } from '../../shared-services/responsive-layout.service';
import { ThemeService } from '../../shared-services/theme.service';
import { formatTooltipValue, getTooltipLabel, buildConfigurableAxisLabel } from '../../utils/chart.utils';
import { matchValue, MatchMode } from '../../utils/filter.utils';

import { CapitalCasePipe } from '../../pipes/capital-case.pipe';

@Component({
  selector: 'app-bubble-chart',
  imports: [CapitalCasePipe],
  standalone: true,
  templateUrl: './bubble-chart.component.html',
  styleUrl: './bubble-chart.component.scss',
})
export class BubbleChartComponent implements AfterViewInit, OnDestroy {
  @Input() data!: any;
  protected myChart!: echarts.ECharts;
  protected allPointNames: string[] = [];

  protected wrapper: ChartWrapperInstance = inject(ChartWrapperService).createInstance();
  protected configService = inject(ConfigService);
  protected globalFilterService = inject(GlobalFilterService);
  protected chartFilterHandler = inject(ChartFilterHandlerService);
  protected responsiveLayoutService = inject(ResponsiveLayoutService);
  protected themeService = inject(ThemeService);
  protected reflowSub?: Subscription;
  protected themeSub?: Subscription;
  private lastChartData?: { points: any[] };

  constructor(
    configService?: ConfigService,
    globalFilterService?: GlobalFilterService,
    chartFilterHandler?: ChartFilterHandlerService,
  ) {
    if (configService) this.configService = configService;
    if (globalFilterService) this.globalFilterService = globalFilterService;
    if (chartFilterHandler) this.chartFilterHandler = chartFilterHandler;
  }

  ngAfterViewInit(): void {
    const initialFilters = this.wrapper.attach(this.data, {
      onReload: (filters) => this.loadAndRenderChart(filters),
      getChart: () => this.myChart,
      getSeriesNames: () => this.allPointNames,
      getCategories: () => this.allPointNames,
      // Custom highlight: bubble uses point-name matching with configurable matchMode
      onHighlightCategories: (cats) => this.highlightMatchingPoints(cats),
      onResetCategoryHighlight: () => this.resetHighlight(),
    });

    this.loadAndRenderChart(initialFilters ?? undefined);

    this.reflowSub = this.responsiveLayoutService.reflow$.subscribe(() => {
      this.myChart?.resize();
    });

    // Re-render with fresh theme tokens when the user switches theme at runtime.
    // Dispose + re-init is required because ECharts' registered 'dvm' theme
    // updates do NOT propagate to already-initialized chart instances.
    this.themeSub = this.themeService.theme$.subscribe(() => {
      if (!this.lastChartData || !this.myChart) return;
      const chartDom = document.getElementById(this.data.widgetId);
      if (!chartDom) return;
      this.myChart.dispose();
      this.myChart = echarts.init(chartDom, 'dvm');
      this.renderChart(this.lastChartData);
    });
  }

  protected loadAndRenderChart(filters?: any): void {
    const chartDom = document.getElementById(this.data.widgetId);
    if (!chartDom) return;

    if (!this.myChart) {
      this.myChart = echarts.init(chartDom, 'dvm');
    }

    this.myChart.showLoading();

    this.configService.getConfig(this.data.apiUrl, this.data, filters)?.subscribe({
      next: (res: any) => {
        if (!res) return;
        const dataKey = this.data?.dataKey;
        const raw = dataKey ? res?.[dataKey] : (res?.data || res);
        const chartData = Array.isArray(raw) ? { points: raw } : raw;
        this.renderChart(chartData);
        this.addResizeListener();
      },
      error: () => {
        this.myChart.showLoading();
      },
    });
  }

  protected renderChart(chartData?: { points: any[] }): void {
    if (chartData) this.lastChartData = chartData;
    const theme = this.themeService.resolve(this.data?.themeOverrides);
    this.allPointNames = (chartData?.points || []).map((p: any) => p[3] || '').filter(Boolean);

    const xAxisOption: any = { scale: true, min: 0 };
    const yAxisOption: any = {
      scale: true,
      min: 60,
      ...(buildConfigurableAxisLabel(this.data?.chartConfig?.yAxis)
        ? { axisLabel: buildConfigurableAxisLabel(this.data?.chartConfig?.yAxis) }
        : {}),
    };

    const option = {
      tooltip: {
        trigger: 'item',
        backgroundColor: theme.tooltip?.bg || '#fff',
        borderColor: theme.tooltip?.borderColor || '#ccc',
        textStyle: {
          color: theme.tooltip?.textColor || '#333',
          fontSize: theme.tooltip?.fontSize || 13,
        },
        formatter: (() => {
          const tooltipCfg = this.data?.chartConfig?.tooltip;
          if (tooltipCfg?.fields?.length) {
            return (params: any) => {
              const d = params.data;
              const lines: string[] = [];
              for (const field of tooltipCfg.fields) {
                const label = getTooltipLabel(field, tooltipCfg.autoCapitalizeKeys !== false);
                const idx = parseInt(field.key, 10);
                const rawValue = !isNaN(idx) ? d[idx] : d[field.key];
                lines.push(`<b>${label}:</b> ${formatTooltipValue(rawValue, field)}`);
              }
              return lines.join('<br/>');
            };
          }
          return (params: any) => {
            const [gdp, lifeExp, pop, country] = params.data;
            return `<strong>${country}</strong><br/>GDP: ${gdp}<br/>Life Expectancy: ${lifeExp}<br/>Population: ${pop}`;
          };
        })(),
      },
      grid: {
        left: '3%',
        right: '4%',
        bottom: '3%',
        containLabel: true,
      },
      xAxis: xAxisOption,
      yAxis: yAxisOption,
      series: [
        {
          type: 'scatter',
          data: chartData?.points,
          symbolSize: (data: any[]) => {
            const pointCount = chartData?.points?.length || 0;
            const raw = Math.sqrt(data[2]) / 100;
            // Single bubble: ensure a visible minimum size for better readability
            if (pointCount === 1) return Math.max(raw, 50);
            return Math.max(raw, 8);
          },
          emphasis: {
            focus: 'series',
            label: {
              show: true,
              formatter: (param: any) => param.data[3],
              position: 'top',
            },
          },
          itemStyle: { color: theme.chart?.palette?.[0] || '#5470C6' },
        },
      ],
    };

    applyRoleVisibilityToOption(option, this.data);

    this.myChart.setOption(option, true);
    setTimeout(() => this.myChart.hideLoading(), 1000);

    // Wrapper wires click events — but we override with custom click for bubble
    this.wrapper.afterRender();
    this.setupCustomClickEvent();
  }

  // Custom click: bubble extracts point name from data[3]
  protected setupCustomClickEvent(): void {
    this.myChart.off('click');
    this.myChart.on('click', (params: any) => {
      if (params.componentType === 'xAxis') {
        const axisIndex = params.xAxisIndex ?? params.axisIndex ?? 0;
        if (axisIndex === 1 && this.data?.enableDoubleXAxis && this.data?.enableXAxis2Click) {
          this.chartFilterHandler.handleDoubleXAxisClick(this.data, params.value);
        } else if (this.data?.enableLabelClick) {
          this.wrapper.handleLabelClick(params.value);
        }
      } else if (params.componentType === 'yAxis') {
        const axisIndex = params.yAxisIndex ?? 0;
        if (axisIndex === 1 && this.data?.enableDoubleYAxis && this.data?.enableYAxis2Click) {
          this.chartFilterHandler.handleDoubleYAxisClick(this.data, params.value);
        }
      } else {
        const raw = params.data?.value || params.data;
        const pointName = raw?.[3] || params.name;
        if (this.data?.enableLabelClick && pointName) {
          this.wrapper.handleLabelClick(pointName);
        } else {
          this.chartFilterHandler.updateFilterSelection(this.data, pointName);
        }
      }
    });
  }

  // Custom highlight: bubble matches point names with configurable matchMode
  protected highlightMatchingPoints(legends: string[]): void {
    const matchMode = (this.data?.chartConfig?.categoryMatchMode || 'contains') as MatchMode;
    const option = this.myChart.getOption() as any;
    if (!option?.series) return;

    const hasAnyMatch = option.series.some((s: any) =>
      s.data?.some((point: any) => {
        const name = (point?.value || point)?.[3] || point?.[3] || '';
        return legends.some((legend: string) => matchValue(name, legend, matchMode));
      })
    );
    if (!hasAnyMatch) return;

    const updatedSeries = option.series.map((s: any) => {
      if (!s.data) return s;
      const newData = s.data.map((point: any) => {
        const raw = point?.value || point;
        const name = raw?.[3] || '';
        const isMatch = legends.some((legend: string) => matchValue(name, legend, matchMode));
        return { value: raw, itemStyle: { opacity: isMatch ? 1 : 0.2 } };
      });
      return { ...s, data: newData };
    });

    this.myChart.setOption({ series: updatedSeries });
  }

  protected resetHighlight(): void {
    const option = this.myChart.getOption() as any;
    if (!option?.series) return;

    const updatedSeries = option.series.map((s: any) => {
      if (!s.data) return s;
      const newData = s.data.map((point: any) => {
        const raw = point?.value || point;
        return { value: raw, itemStyle: { opacity: 1 } };
      });
      return { ...s, data: newData };
    });

    this.myChart.setOption({ series: updatedSeries });
  }

  protected addResizeListener(): void {
    window.addEventListener('resize', this.resizeChart);
  }
  protected resizeChart = () => {
    this.myChart?.resize();
  };

  ngOnDestroy(): void {
    this.wrapper.detach();
    this.reflowSub?.unsubscribe();
    this.themeSub?.unsubscribe();
    window.removeEventListener('resize', this.resizeChart);
    this.myChart?.dispose();
  }
}
